import { getCookie } from './cookies';

export type FeatureModuleKey = 'incidents' | 'meetings' | 'inventories' | 'resources';

export type SurveyInterest = 'oui' | 'peut-etre' | 'non';
export type SurveyBetaOptin = 'oui' | 'non';
export type SurveyUrgency = '1_mois' | '3_mois' | '6_mois';

const HUBSPOT_PORTAL_ID = '145651354';
const HUBSPOT_REGION = 'eu1';

const FEATURE_SURVEY_FORM_IDS: Record<FeatureModuleKey, string> = {
  incidents: 'b5ce4303-0205-484f-8d46-83bb4694d152',
  meetings: '198a779a-5097-427f-ba1d-e84785f22d23',
  inventories: '0588709c-f667-4433-8e13-3b23bfaa510d',
  resources: 'b6ec4aaf-682f-4e07-b749-5a8c294b1301',
};

// Noms internes des champs HubSpot — confirmés le 6 août 2026 via l'API HubSpot.
// horizon_souahite : la coquille est dans le nom interne réel côté HubSpot, ne pas "corriger".
const HUBSPOT_FIELD_NAMES = {
  email: 'email',
  interest: 'interet_fonctionnalite',
  betaOptin: 'volontaire_beta_testeur',
  urgency: 'horizon_souahite',
};

// Les propriétés HubSpot sont des listes déroulantes : les valeurs envoyées doivent
// correspondre EXACTEMENT aux options définies côté HubSpot.
const HUBSPOT_VALUE_MAP = {
  interest: { oui: 'Oui', 'peut-etre': 'Peut-etre', non: 'Non' } as Record<SurveyInterest, string>,
  betaOptin: { oui: 'Oui', non: 'Non' } as Record<SurveyBetaOptin, string>,
  urgency: { '1_mois': '1 mois', '3_mois': '3 mois', '6_mois': '6 mois' } as Record<
    SurveyUrgency,
    string
  >,
};

interface SubmitFeatureSurveyPayload {
  email?: string;
  moduleKey: FeatureModuleKey;
  interest?: SurveyInterest;
  betaOptin?: SurveyBetaOptin;
  urgency?: SurveyUrgency;
}

interface HubSpotField {
  name: string;
  value: string;
}

const readHubspotUtk = (): string | undefined => {
  if (!window.HubSpotConversations?.getPrivacyConsentStatus) return undefined;
  const consent = window.HubSpotConversations.getPrivacyConsentStatus();
  if (consent?.categories?.analytics?.accepted !== true) return undefined;
  return getCookie('hubspotutk');
};

export const submitFeatureSurvey = ({
  email,
  moduleKey,
  interest,
  betaOptin,
  urgency,
}: SubmitFeatureSurveyPayload): void => {
  if (!email) return;

  const formGuid = FEATURE_SURVEY_FORM_IDS[moduleKey];
  const endpoint = `https://api-${HUBSPOT_REGION}.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${formGuid}`;

  const fields: HubSpotField[] = [{ name: HUBSPOT_FIELD_NAMES.email, value: email }];
  if (interest) {
    fields.push({
      name: HUBSPOT_FIELD_NAMES.interest,
      value: HUBSPOT_VALUE_MAP.interest[interest],
    });
  }
  if (betaOptin) {
    fields.push({
      name: HUBSPOT_FIELD_NAMES.betaOptin,
      value: HUBSPOT_VALUE_MAP.betaOptin[betaOptin],
    });
  }
  if (urgency) {
    fields.push({
      name: HUBSPOT_FIELD_NAMES.urgency,
      value: HUBSPOT_VALUE_MAP.urgency[urgency],
    });
  }

  const payload = {
    fields,
    context: {
      hutk: readHubspotUtk(),
      pageUri: window.location.href,
      pageName: document.title,
    },
  };

  fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
    .then(async response => {
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        console.error('[FeatureSurvey] HubSpot submission failed:', response.status, body);
      }
    })
    .catch(error => {
      console.error('[FeatureSurvey] Network error while submitting HubSpot form:', error);
    });
};
